//@flow
import React, {useEffect, useState} from 'react';

import {makeStyles, MenuItem, TableCell} from '@material-ui/core';
import PillSelect from '../../../../../elements/PillSelect';
import {colors} from '../../../../../assets/styles/colors';
import {useCategories} from '../../../../categories/utils';
import {useProductContext} from '../../../context';
import {useEditProduct} from '../../../utils/editProduct';

import type {BulkEditProductCategory, Tax} from '../../../../../entities';
import {getTaxesService} from '../../../../products/promises';
import {debounce} from '../../../../../utils/functions';

const useStyles = makeStyles(theme => ({
  selectField: {
    whiteSpace: 'nowrap',
    padding: '15px',
    borderRadius: '8px',
    height: '48px',
    color: colors.grey800,
    border: `1px solid ${colors.grey300}`,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectLabel: {
    whiteSpace: 'nowrap',
    maxWidth: '18ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  filterItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // width: '250px',
    borderBottom: `1px solid ${colors.grey200}`,
    padding: '0.75rem 1.5rem',

    '&:last-child': {
      borderBottom: 'none',
    },
  },
  filterLabel: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '.9rem',
    maxWidth: '18ch',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  customCheckbox: {
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    border: `1.25px solid ${colors.grey400}`,
    background: 'white',

    '&[data-checked="true"]': {
      border: `6px solid ${colors.primary}`,
    },
  },
}));

export default function CategoryCell() {
  const classes = useStyles();

  const [product, setProduct] = useProductContext(product => {
    return {
      id: product.id,
      categories: product.categories,
      price: product.price,
      taxedPrice: product.taxedPrice,
      taxedOfferPrice: product.taxedOfferPrice,
      taxes: product.taxes ?? [],
      categoryTaxes: product.categoryTaxes,
      hasVariants:
        product.productVariantSetting?.variants &&
        product.productVariantSetting.variants.length > 0,
      variants: product.productVariantSetting?.variants ?? [],
    };
  });
  const categories = useCategories();
  // const setCategoryContext = useSetProductCategoriesContext();

  const {editProduct} = useEditProduct(product ? product.id : '');

  const [baseCategories] = useState<BulkEditProductCategory[]>(
    product?.categories ?? []
  );

  const [categoriesSelected, setCategoriesSelected] = useState<
    BulkEditProductCategory[]
  >(product && product.categories ? product.categories : []);

  useEffect(() => {
    const categoriesData = categories
      .filter(category => categoriesSelected.some(c => c.id === category.id))
      .map(category => ({
        id: category.id,
        name: category.name,
      }));
    setProduct({
      categories: categoriesData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesSelected]);

  if (!product) return null;

  const inputLabel = categoriesSelected
    .map(category => category.name)
    .join(', ');

  const toggleCategory = (category: BulkEditProductCategory) => {
    const selected = categoriesSelected.some(c => c.id === category.id);
    if (selected) {
      setCategoriesSelected(
        categoriesSelected.filter(c => c.id !== category.id)
      );
    } else {
      setCategoriesSelected([...categoriesSelected, category]);
    }
  };

  /** Update pricing references */

  // const calcPriceWithTax = (value: number, taxFactor: number) => {
  //   return parseFloat(value * taxFactor).toFixed(5);
  // };

  const calcPriceWithoutTax = (value: number, taxFactor: number) => {
    return parseFloat(value / taxFactor).toFixed(5);
  };

  const updatePricing = (categoryTaxes: Tax[]) => {
    const categoryTaxesPercentage = categoryTaxes.map(el => el.percentage);
    const taxFactor = [...product.taxes, ...categoryTaxesPercentage]
      .map(parseFloat)
      .reduce((a, b) => a + b, 1);

    const newPrice = product.taxedPrice
      ? calcPriceWithoutTax(parseFloat(product.taxedPrice), taxFactor)
      : '';

    const newOfferPrice = product.taxedOfferPrice
      ? calcPriceWithoutTax(parseFloat(product.taxedOfferPrice), taxFactor)
      : '';

    // variant

    // if (product.hasVariants) {
    // }

    setProduct({
      price: newPrice,
      offerPrice: newOfferPrice,
      taxedPrice: product.taxedPrice,
      taxedOfferPrice: product.taxedOfferPrice,
      categoryTaxes: categoryTaxesPercentage,
    });
  };

  const saveCategories = async () => {
    const categories = categoriesSelected.map(category => category.id);

    const onlyDiffCategories = categories.filter(
      id => !baseCategories.find(el => el.id === id)
    );

    if (onlyDiffCategories.length) {
      try {
        const taxes = await getTaxesService({
          product_categories: onlyDiffCategories.join(','),
          enabled: 1,
        });
        updatePricing(taxes);
      } catch (error) {
        updatePricing([]);
      }
    } else {
      updatePricing([]);
    }

    debounce(_ => {
      editProduct({
        categories: categoriesSelected.map(category => category.id),
      });
    }, 750)();
  };

  return (
    <TableCell align="right">
      <PillSelect
        label={inputLabel || 'Categorias'}
        variant="outlined"
        classes={{
          button: classes.selectField,
          label: classes.selectLabel,
        }}
        onClose={() => {
          saveCategories();
        }}
        disabled={!categories}
      >
        {categories &&
          categories.map(category => {
            const selected = categoriesSelected.some(c => c.id === category.id);
            return (
              <MenuItem
                className={classes.filterItem}
                key={category.id}
                disabled={!category.enabled}
                onClick={() => toggleCategory(category)}
              >
                <span className={classes.filterLabel}>{category.name}</span>
                <span
                  className={classes.customCheckbox}
                  data-checked={selected}
                />
              </MenuItem>
            );
          })}
      </PillSelect>
    </TableCell>
  );
}
